import is from '@sindresorhus/is';

import type { TranslationKey } from '~/shared/internationalization/translation';

import type {
    AiTransformationMatrixPoint,
    AutomationScoreLevel,
} from '../../../data-contract/ai-transformation-strategy';

const LABEL_KEYS: Readonly<Record<AutomationScoreLevel, TranslationKey>> = {
    HIGH: 'pages.ai-transformation.strategy.score-level.high',
    LOW: 'pages.ai-transformation.strategy.score-level.low',
    MEDIUM: 'pages.ai-transformation.strategy.score-level.medium',
    VERY_HIGH: 'pages.ai-transformation.strategy.score-level.very-high',
};

/**
 * The one place a score level becomes words. The backend buckets the scores and orders the levels (the filters
 * response lists them); an item without a level is not assessed.
 */
export const scoreLevelLabelKey = (level: AutomationScoreLevel | null | undefined): TranslationKey =>
    is.nullOrUndefined(level) ? 'pages.ai-transformation.strategy.score-level.not-assessed' : LABEL_KEYS[level];

/** The levels whose tasks a prioritized role, level or position takes along. */
export const HIGH_SCORE_LEVELS: readonly AutomationScoreLevel[] = ['HIGH', 'VERY_HIGH'];

/** The assessed levels a prioritized scope leaves for the user to add. */
export const OTHER_SCORE_LEVELS: readonly AutomationScoreLevel[] = ['LOW', 'MEDIUM'];

/** The Co Studio automation potential colours, `none` for what is not assessed. */
export type PotentialTone = 'high' | 'low' | 'moderate' | 'none' | 'very-high';

const TONES: Readonly<Record<AutomationScoreLevel, PotentialTone>> = {
    HIGH: 'high',
    LOW: 'low',
    MEDIUM: 'moderate',
    VERY_HIGH: 'very-high',
};

export const potentialToneOf = (level: AutomationScoreLevel | null | undefined): PotentialTone =>
    is.nullOrUndefined(level) ? 'none' : TONES[level];

/**
 * The level most of a group's positions sit at, the higher one of equals; undefined without any. A role or level on the
 * matrix takes its colour from it, as the backend scores only positions and tasks.
 */
export const dominantScoreLevel = (
    counts: ReadonlyMap<AutomationScoreLevel, number>,
    order: readonly AutomationScoreLevel[],
): AutomationScoreLevel | undefined =>
    order.reduce<AutomationScoreLevel | undefined>((best, level) => {
        const count = counts.get(level) ?? 0;
        if (count === 0) return best;

        return best === undefined || count >= (counts.get(best) ?? 0) ? level : best;
    }, undefined);

/**
 * The level each point stands at from the score level counts the backend sends with it, taking only `order`'s levels;
 * undefined when the backend sends none, so the caller reads the levels another way.
 */
export const scoreLevelsOfPoints = (
    points: readonly AiTransformationMatrixPoint[],
    order: readonly AutomationScoreLevel[],
): ReadonlyMap<string, AutomationScoreLevel> | undefined => {
    if (points.some(({ scoreLevelCounts }) => scoreLevelCounts === undefined)) return undefined;

    return new Map(
        points.flatMap(({ id, scoreLevelCounts }) => {
            const level = dominantScoreLevel(
                new Map(scoreLevelCounts?.map(({ count, level }) => [level, count])),
                order,
            );
            return level === undefined ? [] : [[id, level] as const];
        }),
    );
};
